import { createAction } from 'redux-actions';
import { MATFILE_ENDLESS_TYPE } from 'pxc-editor-core/constants';
import { INTERACTION_MODE } from 'pxc-editor-material-svg-rendering/constants';
import Section from 'pxc-editor-core/entities/Section';
import {
  getAllLayers,
  getShieldSectionsArray,
  createSectionSmart,
} from 'pxc-editor-core/helpers/schema';
import { measureTextWidth } from 'pxc-editor-material-svg-rendering/helpers/calculation';
import { generateFontString } from 'pxc-editor-material-svg-rendering/helpers/general';
import {
  DELETE_SECTION,
  DELETE_SECTIONS,
  SET_MODE,
  SET_PRESELECTED_PRINTER,
  SET_PRINTER_LIST,
  SET_EDITOR_TAB,
  SET_CELL_SELECTION_TYPE,
  SET_GROUP_SELECTION_TYPE,
  ADD_SHIELD_TO_SELECTION,
  CLEAR_SHIELDS_SELECTION,
  CLEAR_SECTIONS_SELECTION,
  REMOVE_SHIELD_FROM_SELECTION,
  REMOVE_SECTION_FROM_SELECTION,
  SET_EDITOR_MENU_TAB,
  SET_COLORED_STATE,
  SET_MULTI_COLORED_STATE,
  SET_SECTIONS_DISPLAY_STATE,
  SET_GRID_DISPLAY_STATE,
  SET_GRID_WIDTH,
  SET_INPUT_DISPLAY_STATE,
  SET_TEXT_TYPING_RESOURCE,
  BULK_SHAPE_SELECTION,
  CLEAR_GROUP_SELECTION,
  SET_TARGET_DEVICE,
  RESET_STATE,
  ENDLESS_BASED_ON_CONTENT,
  SINGLE_CELLS,
  ZOOM_OUT_STATE,
  SET_PROJECT_NAME,
  SET_SHOW_LOADER_STATE,
  SET_CONTENT_PLACING_DEBUGGING_STATE,
  SET_DEBUGGING_GRID_STATE,
  SET_FROM_WIZARD_STATE,
  SET_WIZARD_DATA,
  COPY_SEL_WIZARD_CONTENT,
  CMME_STATES,
  SET_IS_CABLE_FLAG_SYNC,
  SET_USER_SIZE_UNIT,
  SWITCH_SELECTED_SECTION,
  SWITCH_SELECTED_SHIELD,
  SET_USER_DEVICE_MANIPULATOR_TYPE,
  USER_DEVICE_MANIPULATOR_TYPE,
  SET_AUTOFILL_AND_COPPY_PATTERN,
  SET_FICTIVE_SECTIONS,
  ADD_SECTION_TO_MATRIX_SELECTION,
  REMOVE_SECTION_FROM_MATRIX_SELECTION,
  SET_AUTOFILL_MATRIX_TEMPLATE,
  SET_FONT_COEF,
  ENDLESS_FIXED,
  DESKTOP,
  APP_ENVIRONMENT,
  SET_SHOW_INSERT_MODAL_STATE,
  SET_PLATFORM_ENVIRONMENT,
  SET_FONT_SIZE_COEF,
  SET_LINE_HEIGHT_COEF,
  SET_LETTER_SPACING_COEF,
  SET_VARIANT_COLOR_MODAL_OPEN,
  SET_FLOATABLE_INPUT_VISIBLE,
  SET_PRINTING,
  REMOVE_BLUR_FROM_ALL_INPUTS,
} from '@/constants';
import { getBuildedMatfileTemplate } from '@/helpers/matfile';
import {
  detectTargetDevice,
  recalcZoomBordersScaleCoefs,
  getPaginationPageItemsByPage,
  getFirstSelectedSection,
  getShield,
  getActiveElement,
  getElementByQuerySelector,
  getCurrentEnvironment,
  calculateFontSizeCoef,
  getShieldsByGroupId,
  getSectionsByShields,
  getElementsByQuerySelectorAll,
  getSelectedSection,
} from '@/helpers';
import { setPageLength } from '@/actions';
import { resetGlobalSettingsAction } from '@/store/actions/app-settings';
import setMatrixSectionWidthThunk from '@/store/actions/matrix/set-matrix-section-width';
import { createSnapshotThunk } from './undoRedo';
import {
  checkSyncSlaveShieldByMasterShield,
  checkSyncSlaveShieldBySection,
  overridePages,
  overridePageAction,
} from './page';
import { handleZoomClick, handleZoomStateChange } from './rendererView';
import { checkPageContentLength, setPageResizeTypeAction } from './pageResize';
import {
  editSection,
  updateSectionProp,
  setTextInSection,
  addSectionAction,
  addSelectedSectionThunk,
  addSelectedSections,
  addNewSectionsAction,
  deleteSelectedSectionsAction,
  applyGlobalSectionSettingsToSection,
} from './section';

const currentEnvironment = getCurrentEnvironment();

export const setContentPlacingDebuggingState = isContentPlacingDebugging => dispatch => {
  dispatch(
    createAction(
      SET_CONTENT_PLACING_DEBUGGING_STATE,
      () => isContentPlacingDebugging
    )()
  );
};

export const setDebuggingGridState = isDebuggingGrid => dispatch => {
  dispatch(createAction(SET_DEBUGGING_GRID_STATE, () => isDebuggingGrid)());
};

export const setFromWizardState = isFromWizard => dispatch => {
  dispatch(createAction(SET_FROM_WIZARD_STATE, () => isFromWizard)());
};

export const setWizardData = data => dispatch => {
  dispatch(createAction(SET_WIZARD_DATA, () => data)());
};

export const copyWizardData = data => dispatch => {
  dispatch(createAction(COPY_SEL_WIZARD_CONTENT, () => data)());
};

export const setCmmeStates = data => dispatch => {
  dispatch(createAction(CMME_STATES, () => data)());
};

export const setIsCableFlagSyncState = state => dispatch => {
  dispatch(createAction(SET_IS_CABLE_FLAG_SYNC, () => state)());
};

export const setShowInsertModalState = isShowInsertModal => dispatch => {
  dispatch(
    createAction(SET_SHOW_INSERT_MODAL_STATE, () => isShowInsertModal)()
  );
};

export const setShowLoader = isShowLoader => dispatch => {
  dispatch(createAction(SET_SHOW_LOADER_STATE, () => isShowLoader)());
};

export const setProjectName = projectName => dispatch => {
  dispatch(createAction(SET_PROJECT_NAME, () => projectName)());
};

export const setUserSizeUnit = userSizeUnit => dispatch => {
  dispatch(createAction(SET_USER_SIZE_UNIT, () => userSizeUnit)());
};

export const setIsShowMatrixBorderThunk = selectedShield => (
  dispatch,
  getState
) => {
  const {
    core: { pages, pagesOrder, pagesSelection },
  } = getState();

  dispatch(createSnapshotThunk());

  const currentPage = pages[pagesSelection[0]];

  const { layouts } = currentPage;
  for (let layoutId in layouts) {
    const { groups } = layouts[layoutId];
    for (let groupId in groups) {
      const { shields } = groups[groupId];
      for (let shId in shields) {
        const shield = shields[shId];
        if (shield.id === selectedShield.id) {
          shield.isMatrixBordersEnabled = !selectedShield.isMatrixBordersEnabled;
          break;
        }
      }
    }
  }

  const updatedPages = {
    ...pages,
    [currentPage.uid]: { ...currentPage, layouts },
  };

  dispatch(overridePages(updatedPages, pagesOrder, false));
};

export const addNewSections = () => (dispatch, getState) => {
  dispatch(createSnapshotThunk());

  const {
    core: { pages, pagesSelection, ribbonColor, shieldsSelection },
  } = getState();
  const currentPage = pages[pagesSelection[0]];
  const sections = Object.entries(shieldsSelection).reduce(
    (acc, [key, value]) => {
      const section = createSectionSmart({
        sourceShield: value,
        pages,
        ribbonColor,
      });
      return { ...acc, [section.id]: section };
    },
    {}
  );

  dispatch(
    addNewSectionsAction({
      page: {
        ...currentPage,
        sections: { ...currentPage.sections, ...sections },
      },
      sectionsSelection: sections,
    })
  );
};

export const appendSection = () => (dispatch, getState) => {
  const {
    core: { shieldsSelection },
  } = getState();
  let selectedShield = null;
  for (const shieldId in shieldsSelection) {
    selectedShield = shieldsSelection[shieldId];
    break;
  }
  dispatch(
    appendSectionAtShield({
      shield: selectedShield,
      isUndoable: false,
      setSelected: true,
    })
  );
};

export const appendMatrixSeparator = position => (dispatch, getState) => {
  const {
    core: {
      sectionsSelection,
      pages,
      pagesSelection,
      isMultiColored,
      paperColor,
      ribbonColor,
      separatorWidth = 0.5,
    },
  } = getState();
  const firstSelSection = getFirstSelectedSection(sectionsSelection, pages);

  if (pagesSelection.length && pages[pagesSelection[0]] && firstSelSection) {
    const page = pages[pagesSelection[0]];
    const { layouts } = page;
    const shield = getShield(layouts, firstSelSection.shieldId);
    const sections = JSON.parse(JSON.stringify(page.sections));

    if (
      shield &&
      Object.keys(sectionsSelection).length &&
      sections &&
      layouts
    ) {
      //1. find place to add new section
      const { text_height, transforms } = getAllLayers(
        shield.id,
        pages
      ).group.textStyle;
      const matrixRotation =
        transforms?.angle === 90 || transforms?.angle === 270;

      const revert = transforms?.angle === 180 || transforms?.angle === 270;

      const placeToAdd = {
        x: matrixRotation
          ? shield.primitiveProps.x
          : (revert && position === 'right') || (!revert && position === 'left')
          ? toFixedX(firstSelSection.x, 10)
          : toFixedX(firstSelSection.x + firstSelSection.width, 10),
        y: matrixRotation
          ? (revert && position === 'right') || (!revert && position === 'left')
            ? toFixedX(firstSelSection.y, 10)
            : toFixedX(firstSelSection.y + firstSelSection.height, 10)
          : shield.primitiveProps.y,
      };

      const shieldX = shield.primitiveProps.x;
      const shieldY = shield.primitiveProps.y;
      let minY = firstSelSection.y;
      let maxY = minY + firstSelSection.height;
      let maxSectionY = firstSelSection.y;
      let minX = firstSelSection.x;
      let maxX = minX + firstSelSection.width;
      let maxSectionX = firstSelSection.x;

      Object.values(sections)
        .filter(s => s.shieldId === shield.id)
        .forEach(section => {
          maxSectionX = maxSectionX < section.x ? section.x : maxSectionX;
          minX = minX > section.x ? section.x : minX;
          maxX =
            maxX < section.x + section.width ? section.x + section.width : maxX;
          maxSectionY = maxSectionY < section.y ? section.y : maxSectionY;
          minY = minY > section.y ? section.y : minY;
          maxY =
            maxY < section.y + section.height
              ? section.y + section.height
              : maxY;
        });

      //2. create new section
      const newSec = createSectionSmart({
        sourceShield: shield,
        pages,
        ribbonColor: !isMultiColored && ribbonColor,
        paperColor: null,
      });
      if (newSec) {
        dispatch(createSnapshotThunk());
        newSec.setX(placeToAdd.x);
        newSec.setY(placeToAdd.y);
        newSec.setWidth(
          matrixRotation ? shield.primitiveProps.w : separatorWidth
        );
        newSec.setHeight(
          matrixRotation ? separatorWidth : shield.primitiveProps.h
        );
        newSec.setFontSize(firstSelSection.fontSize);
        newSec.setChanged(true);
        newSec.setText('');
        newSec.setType('separator');
        newSec.setNewIdById(firstSelSection.id);
        newSec.setChildren(null);

        if (matrixRotation) {
          Object.values(sections)
            .filter(s => s.shieldId === shield.id)
            .forEach(section => {
              if (toFixedX(section.y, 10) >= placeToAdd.y) {
                section.y = toFixedX(section.y + newSec.height, 10);
              } else if (
                toFixedX(section.y + section.height, 10) >
                toFixedX(placeToAdd.y + newSec.height, 10)
              ) {
                section.height = toFixedX(section.height + newSec.height, 10);
              }
              if (section.children) {
                section.children.forEach(child => {
                  if (toFixedX(child.y, 10) >= placeToAdd.y) {
                    child.y = toFixedX(child.y + newSec.height, 10);
                  } else if (
                    toFixedX(child.y + child.height, 10) > placeToAdd.y
                  ) {
                    child.height = toFixedX(child.height + newSec.height, 10);
                  }
                });
              }
            });
          sections[newSec.id] = newSec;

          if (!shield.isMatrixFixedCellWidth) {
            const factor =
              shield.primitiveProps.h /
              (shield.primitiveProps.h + newSec.height);
            Object.values(sections)
              .filter(s => s.shieldId === shield.id)
              .forEach(section => {
                const offSetY = section.y - shield.primitiveProps.y;
                section.y = toFixedX(
                  shield.primitiveProps.y + offSetY * factor,
                  10
                );
                section.height = toFixedX(section.height * factor, 10);
                if (section.children) {
                  section.children.forEach(child => {
                    const offSetY = child.y - shield.primitiveProps.y;
                    child.y = toFixedX(
                      shield.primitiveProps.y + offSetY * factor,
                      10
                    );
                    child.height = toFixedX(child.height * factor, 10);
                  });
                }
              });
          }
        } else {
          Object.values(sections)
            .filter(s => s.shieldId === shield.id)
            .forEach(section => {
              if (toFixedX(section.x, 10) >= placeToAdd.x) {
                section.x = toFixedX(section.x + newSec.width, 10);
              } else if (
                toFixedX(section.x + section.width, 10) >
                toFixedX(placeToAdd.x + newSec.width, 10)
              ) {
                section.width = toFixedX(section.width + newSec.width, 10);
              }
              if (section.children) {
                section.children.forEach(child => {
                  if (toFixedX(child.x, 10) >= placeToAdd.x) {
                    child.x = toFixedX(child.x + newSec.width, 10);
                  } else if (
                    toFixedX(child.x + child.width, 10) > placeToAdd.x
                  ) {
                    child.width = toFixedX(child.width + newSec.width, 10);
                  }
                });
              }
            });
          sections[newSec.id] = newSec;

          if (!shield.isMatrixFixedCellWidth) {
            const factor = (maxX - shieldX) / (maxX - shieldX + newSec.width);
            Object.values(sections)
              .filter(s => s.shieldId === shield.id)
              .forEach(section => {
                const offSetX = section.x - shield.primitiveProps.x;
                section.x = toFixedX(
                  shield.primitiveProps.x + offSetX * factor,
                  10
                );
                section.width = toFixedX(section.width * factor, 10);
                if (section.children) {
                  section.children.forEach(child => {
                    const offSetX = child.x - shield.primitiveProps.x;
                    child.x = toFixedX(
                      shield.primitiveProps.x + offSetX * factor,
                      10
                    );
                  });
                }
              });
          }
        }
      }
      page.sections = sections;
      dispatch(addSectionAction(newSec.toDict ? newSec.toDict() : newSec));
      dispatch(clearSectionsSelection());
      setTimeout(() => {
        dispatch(handleSectionClick({ section: newSec }));
      }, 0);
      if (page?.uid?.length) {
        dispatch(checkPageContentLength(page.uid));
      }
    }
  }
};

export const compareD = (number1, number2) => {
  let tempLeft = toFixedX(number1, 10);
  let tempRight = toFixedX(number2, 10);
  let factor = toFixedX(
    tempLeft > tempRight ? tempLeft / tempRight : tempRight / tempLeft,
    10
  );
  return factor === 1;
};

export const toFixedX = (value, decimals = 1) => {
  const result =
    Math.round((0 + value) * Math.pow(10, decimals)) / Math.pow(10, decimals);
  return result;
};

export const getSectionBounds = (selSec, decimals = 5) => {
  return {
    x: toFixedX(selSec.x, decimals),
    xx: toFixedX(selSec.x + selSec.width, decimals),
    y: toFixedX(selSec.y, decimals),
    yy: toFixedX(selSec.y + selSec.height, decimals),
    w: toFixedX(selSec.width, decimals),
    h: toFixedX(selSec.height, decimals),
  };
};

export const cleanSectionsAxes = shield => (dispatch, getState) => {
  const {
    core: { pages },
  } = getState();
  if (shield) {
    const layers = getAllLayers(shield.id, pages);
    const diffX = {};
    const diffY = {};
    const diffW = {};
    const diffH = {};
    Object.values(layers.page.sections)
      .filter(s => s.shieldId === shield.id)
      .map(s => {
        diffX[toFixedX(s.x, 7)] = s.x;
        diffY[toFixedX(s.y, 7)] = s.y;
        diffW[toFixedX(s.width, 7)] = s.width;
        diffH[toFixedX(s.height, 7)] = s.height;
      });
    Object.values(layers.page.sections)
      .filter(s => s.shieldId === shield.id)
      .forEach(section => {
        section.x = diffX[toFixedX(section.x, 7)];
        section.y = diffY[toFixedX(section.y, 7)];
        section.width = diffW[toFixedX(section.width, 7)];
        section.height = diffH[toFixedX(section.height, 7)];
      });
  }
};

export const getTargetArea = (selSec, sections, rotated) => {
  // detect rows count / resizable area
  const x = rotated ? 'y' : 'x';
  const y = rotated ? 'x' : 'y';
  const width = rotated ? 'height' : 'width';
  const height = rotated ? 'width' : 'height';
  const selRowColumns = sections
    .filter(s => s.shieldId === selSec.shieldId)
    .filter(
      s => compareD(s[y], selSec[y]) && compareD(s[height], selSec[height])
    )
    .sort((a, b) => a[x] - b[x]);
  const separators = sections
    .filter(s => s.shieldId === selSec.shieldId)
    .filter(s => s.type === 'separator')
    .sort((a, b) => a[x] - b[x]);
  let result = {
    l: toFixedX(selSec[x], 10),
    r: toFixedX(selSec[x], 10) + toFixedX(selSec[width], 10),
  };

  if (separators?.length) {
    separators.forEach(sep => {
      const intersected = selRowColumns.filter(
        s => s[x] <= sep[x] && s[x] + s[width] >= sep[x] + sep[width]
      );
      if (!intersected?.length > 0) {
        selRowColumns.push(sep);
      }
    });
  }
  let topRowColumns = [];
  selRowColumns.forEach(section => {
    topRowColumns.push({
      id: section.id,
      left: toFixedX(section[x], 10),
      right: toFixedX(section[x], 10) + toFixedX(section[width], 10),
    });
  });
  topRowColumns = topRowColumns.sort((a, b) => a.left - b.left);
  let index = topRowColumns.findIndex(object => {
    return object.id === selSec.id;
  });

  if (index > -1) {
    let left = index;
    let right = index;
    if (index < topRowColumns.length) {
      for (let i = index; i > 0; i--) {
        if (compareD(topRowColumns[i - 1].right, topRowColumns[i].left)) {
          left = i - 1;
        } else {
          break;
        }
      }
    }

    if (index < topRowColumns.length - 1) {
      for (let i = index; i < topRowColumns.length - 1; i++) {
        if (compareD(topRowColumns[i].right, topRowColumns[i + 1].left)) {
          right = i + 1;
        } else {
          break;
        }
      }
    }
    result.l = toFixedX(topRowColumns[left].left, 10);
    result.r = toFixedX(topRowColumns[right].right, 10);
  } else {
    result.l = 0;
    result.r = 0;
  }
  return result;
};

export const getCellFormat = section => {
  return section
    ? {
        fontSize: section.fontSize,
        isBold: section.isBold,
        isItalic: section.isItalic,
        isUnderline: section.isUnderline,
        font: section.font,
        horizontalAlignment: section.horizontalAlignment,
        verticalAlignment: section.verticalAlignment,
        fontColor: section.fontColor,
      }
    : null;
};

export const appendMatrixRow = type => (dispatch, getState) => {
  const {
    core: { sectionsSelection, pages, pagesSelection },
  } = getState();
  const selectedSection = getFirstSelectedSection(sectionsSelection, pages);

  if (pagesSelection.length && pages[pagesSelection[0]] && selectedSection) {
    const page = pages[pagesSelection[0]];
    const { layouts } = page;
    const shield = getShield(layouts, selectedSection.shieldId);
    const shieldSections = getShieldSectionsArray(shield, page.sections);

    if (shield.isMatrix && shieldSections?.length > 0) {
      // calculation for matrix only
      const selSec = JSON.parse(JSON.stringify(selectedSection));
      const { text_height, transforms } = getAllLayers(
        shield.id,
        pages
      ).group.textStyle;
      const matrixRotation =
        transforms?.angle === 90 || transforms?.angle === 270;
      const targetArea = getTargetArea(
        selSec,
        Object.values(shieldSections),
        matrixRotation
      );
      const x = matrixRotation ? 'y' : 'x';
      const y = matrixRotation ? 'x' : 'y';
      const width = matrixRotation ? 'height' : 'width';
      const height = matrixRotation ? 'width' : 'height';
      const h = matrixRotation ? 'w' : 'h';
      const cellHeightBefore = selectedSection[height];

      const minY = shieldSections.sort((a, b) => a[y] - b[y])[0][y];
      const targetAreaCells = shieldSections.filter(
        s =>
          toFixedX(s[x], 5) >= toFixedX(targetArea.l, 5) &&
          toFixedX(s[x] + s[width], 5) <= toFixedX(targetArea.r, 5)
      );
      const rowsY = [
        ...new Set(targetAreaCells.map(s => toFixedX(s[y], 5))),
      ].sort((a, b) => a - b);
      const selRowColumns = targetAreaCells
        .filter(s => compareD(s[y], selSec[y]))
        .sort((a, b) => a - b);
      const newRowColumns = JSON.parse(
        JSON.stringify(selRowColumns.filter(c => c.type !== 'separator'))
      );

      const rowsCount = rowsY.length > 0 ? rowsY.length : 1;
      const rowHeight = toFixedX(shield.primitiveProps[h] / rowsCount, 10);
      const newRowsCount = rowsCount + 1;
      const newRowHeight = toFixedX(
        shield.primitiveProps[h] / newRowsCount,
        10
      );

      const selOffSetY = selSec[y] - minY;
      const rotatedType = matrixRotation
        ? (transforms?.angle === 90 && type === 'bottom') ||
          (transforms?.angle === 270 && type === 'top')
        : (transforms?.angle === 0 && type === 'top') ||
          (transforms?.angle === 180 && type === 'bottom');
      const newRow = toFixedX(
        (selOffSetY + (rotatedType ? 0 : rowHeight)) / rowHeight,
        0
      );

      if (newRowColumns?.length) {
        dispatch(createSnapshotThunk());
        setTimeout(() => {
          // make place for new row
          targetAreaCells.forEach(cell => {
            if (cell.type !== 'separator') {
              const offSetY = cell[y] - minY;
              const isRow = toFixedX(offSetY / rowHeight, 0);
              if (
                rotatedType &&
                toFixedX(cell[y], 5) >= toFixedX(selSec[y], 5)
              ) {
                cell[y] = toFixedX(minY + (isRow + 1) * newRowHeight, 10);
              } else if (toFixedX(cell[y], 5) > toFixedX(selSec[y], 5)) {
                cell[y] = toFixedX(minY + (isRow + 1) * newRowHeight, 10);
              } else {
                cell[y] = toFixedX(minY + isRow * newRowHeight, 10);
              }
              cell[height] = newRowHeight;
              if (cell.children) {
                cell.children.forEach(child => {
                  child[y] = cell[y];
                  child[height] = cell[height];
                });
              }
            }
          });
          queueMicrotask(() => {
            newRowColumns.forEach(cell => {
              cell[y] = toFixedX(minY + newRow * newRowHeight, 10);
              cell[height] = newRowHeight;
              dispatch(
                appendMatrixSectionAtShield({
                  shield,
                  x: cell.x,
                  y: cell.y,
                  cellWidth: cell.width,
                  cellHeight: cell.height,
                  text: '',
                  children: null,
                  isSeparator: false,
                  setSelectd: false,
                  cellFormat: getCellFormat(cell),
                })
              );
            });
            dispatch(adaptMatrixFontSize(shield, cellHeightBefore, targetArea));
            dispatch(cleanSectionsAxes(shield));
          });

          setTimeout(() => {
            const {
              core: { pages, pagesSelection },
            } = getState();
            const afterUpdatePage = pages[pagesSelection[0]],
              beforeUpdatePage = page;
            const beforeUpdateKeyCollection = Object.keys(
              beforeUpdatePage.sections
            );
            const sections = Object.values(afterUpdatePage.sections)
              .filter(
                section => !beforeUpdateKeyCollection.includes(section.id)
              )
              .sort((a, b) => {
                if (matrixRotation) {
                  return a.x === b.x ? a.y - b.y : a.x - b.x;
                } else {
                  return a.y === b.y ? a.x - b.x : a.y - b.y;
                }
              });

            if (sections[0])
              dispatch(handleSectionClick({ section: sections[0] }));
          }, 0);
        }, 0);
      }
    }
  }
};

const setMatrixSectionWidth = ({
  section: firstSelSection,
  width: newLength
}) => (
  dispatch,
  getState
) => {
  const {
    core: { sectionsSelection, pages, pagesSelection },
  } = getState();

  if (pagesSelection.length && pages[pagesSelection[0]] && firstSelSection) {
    const page = pages[pagesSelection[0]];
    const { layouts } = page;
    const shield = getShield(layouts, firstSelSection.shieldId);
    const sections = JSON.parse(JSON.stringify(page.sections));

    if (
      shield &&
      Object.keys(sectionsSelection).length &&
      sections &&
      layouts
    ) {
      const { text_height, transforms } = getAllLayers(
        shield.id,
        pages
      ).group.textStyle;
      const matrixRotation =
        transforms?.angle === 90 || transforms?.angle === 270;

      if (matrixRotation) {
        const shieldStart = shield.primitiveProps.y;
        const scaleFactor = shield.isMatrixFixedCellWidth
          ? 1
          : (shield.primitiveProps.h - newLength) /
            (shield.primitiveProps.h - firstSelSection.height);
        newLength = shield.isMatrixFixedCellWidth
          ? newLength
          : newLength / scaleFactor;

        const y = toFixedX(firstSelSection.y, 10);
        const yr = toFixedX(firstSelSection.y + firstSelSection.height, 10);
        const yrn = toFixedX(firstSelSection.y + newLength, 10);
        const yro = toFixedX(yr > yrn ? yr - yrn : yrn - yr, 10);

        dispatch(createSnapshotThunk());
        Object.values(sections)
          .filter(s => s.shieldId === shield.id)
          .forEach(section => {
            const sec_left = toFixedX(section.y, 10);
            const sec_right = toFixedX(section.y + section.height, 10);
            if (yr > yrn) {
              if (sec_left <= y && sec_right > y) {
                section.height = toFixedX(section.height - yro, 10);
              } else if (sec_left >= yr) {
                section.y = toFixedX(section.y - yro, 10);
              } else if (sec_left > y) {
                section.y = toFixedX(section.y - yro, 10);
              }
              if (section.children) {
                section.children.forEach(child => {
                  const ch_left = toFixedX(child.y, 10);
                  const ch_right = toFixedX(child.y + child.height, 10);
                  if (ch_left <= y && ch_right > y) {
                    child.height = toFixedX(child.height - yro, 10);
                  } else if (ch_left >= yr) {
                    child.y = toFixedX(child.y - yro, 10);
                  } else if (ch_left > y) {
                    child.y = toFixedX(child.y - yro, 10);
                  }
                });
              }
            } else {
              if (toFixedX(sec_left, 10) >= toFixedX(yr, 10)) {
                section.y = toFixedX(section.y + yro, 10);
              } else if (
                (toFixedX(sec_left, 10) === toFixedX(y, 10) &&
                  toFixedX(sec_right, 10) === toFixedX(yr, 10)) ||
                (toFixedX(sec_left, 10) < toFixedX(yr, 10) &&
                  toFixedX(sec_right, 10) >= toFixedX(yr, 10))
              ) {
                section.height = toFixedX(section.height + yro, 10);
              }
              if (section.children) {
                section.children.forEach(child => {
                  const ch_left = toFixedX(child.y, 10);
                  const ch_right = toFixedX(child.y + child.height, 10);
                  if (toFixedX(ch_left, 10) >= toFixedX(yr, 10)) {
                    child.y = toFixedX(child.y + yro, 10);
                  } else if (
                    (toFixedX(ch_left, 10) === toFixedX(y, 10) &&
                      toFixedX(ch_right, 10) === toFixedX(yr, 10)) ||
                    (toFixedX(ch_left, 10) < toFixedX(yr, 10) &&
                      toFixedX(ch_right, 10) >= toFixedX(yr, 10))
                  ) {
                    child.height = toFixedX(child.height + yro, 10);
                  }
                });
              }
            }
          });

        if (!shield?.isMatrixFixedCellWidth) {
          Object.values(sections)
            .filter(s => s.shieldId === shield.id)
            .forEach(section => {
              const offsetY = toFixedX(section.y - shieldStart, 10);

              section.y = toFixedX(shieldStart + offsetY * scaleFactor, 10);
              section.height =
                section.height > 0
                  ? toFixedX(section.height * scaleFactor, 10)
                  : 0;
              if (section.children) {
                section.children.forEach(child => {
                  const childY = child.y - shieldStart;
                  child.y = toFixedX(shieldStart + childY * scaleFactor, 10);
                  child.height = toFixedX(child.height * scaleFactor, 10);
                });
              }

              page.sections = sections;
              const changedSection = Section.fromDict(section).clone();
              if (section.children) changedSection.children = section.children;
              changedSection.setHeight(section.height);
              dispatch(editSection(changedSection.toDict()));
              if (section.id === firstSelSection.id) {
                firstSelSection.y = section.y;
                firstSelSection.height = section.height;
              }
            });
          page.sections = sections;
          dispatch(createSnapshotThunk());
        }
      } else {
        const shieldStart = shield.primitiveProps.x;
        const scaleFactor = shield.isMatrixFixedCellWidth
          ? 1
          : (shield.primitiveProps.w - newLength) /
            (shield.primitiveProps.w - firstSelSection.width);
        newLength = shield.isMatrixFixedCellWidth
          ? newLength
          : newLength / scaleFactor;

        const x = toFixedX(firstSelSection.x, 10);
        const xr = toFixedX(firstSelSection.x + firstSelSection.width, 10);
        const xrn = toFixedX(firstSelSection.x + newLength, 10);
        const xro = toFixedX(xr > xrn ? xr - xrn : xrn - xr, 10);
        dispatch(createSnapshotThunk());
        Object.values(sections)
          .filter(s => s.shieldId === shield.id)
          .forEach(section => {
            const sec_left = toFixedX(section.x, 10);
            const sec_right = toFixedX(section.x + section.width, 10);
            if (xr > xrn) {
              if (sec_left <= x && sec_right > x) {
                section.width = toFixedX(section.width - xro, 10);
              } else if (sec_left >= xr) {
                section.x = toFixedX(section.x - xro, 10);
              } else if (sec_left > x) {
                section.x = toFixedX(section.x - xro, 10);
              }
              if (section.children) {
                section.children.forEach(child => {
                  const ch_left = toFixedX(child.x, 10);
                  const ch_right = toFixedX(child.x + child.width, 10);
                  if (ch_left <= x && ch_right > x) {
                    child.width = toFixedX(child.width - xro, 10);
                  } else if (ch_left >= xr) {
                    child.x = toFixedX(child.x - xro, 10);
                  } else if (ch_left > x) {
                    child.x = toFixedX(child.x - xro, 10);
                  }
                });
              }
            } else {
              if (toFixedX(sec_left, 10) >= toFixedX(xr, 10)) {
                section.x = toFixedX(section.x + xro, 10);
              } else if (
                (toFixedX(sec_left, 10) === toFixedX(x, 10) &&
                  toFixedX(sec_right, 10) === toFixedX(xr, 10)) ||
                (toFixedX(sec_left, 10) < toFixedX(xr, 10) &&
                  toFixedX(sec_right, 10) >= toFixedX(xr, 10))
              ) {
                section.width = toFixedX(section.width + xro, 10);
              }
              if (section.children) {
                section.children.forEach(child => {
                  const ch_left = toFixedX(child.x, 10);
                  const ch_right = toFixedX(child.x + child.width, 10);
                  if (toFixedX(ch_left, 10) >= toFixedX(xr, 10)) {
                    child.x = toFixedX(child.x + xro, 10);
                  } else if (
                    (toFixedX(ch_left, 10) === toFixedX(x, 10) &&
                      toFixedX(ch_right, 10) === toFixedX(xr, 10)) ||
                    (toFixedX(ch_left, 10) < toFixedX(xr, 10) &&
                      toFixedX(ch_right, 10) >= toFixedX(xr, 10))
                  ) {
                    child.width = toFixedX(child.width + xro, 10);
                  }
                });
              }
            }
          });

        if (!shield?.isMatrixFixedCellWidth) {
          Object.values(sections)
            .filter(s => s.shieldId === shield.id)
            .forEach(section => {
              const offsetX =
                section.x >= shieldStart ? section.x - shieldStart : 0;

              section.x = toFixedX(shieldStart + offsetX * scaleFactor, 10);
              section.width =
                section.width > 0
                  ? toFixedX(section.width * scaleFactor, 10)
                  : 0;
              if (section.children) {
                section.children.forEach(child => {
                  const childX = child.x - shieldStart;
                  child.x = toFixedX(shieldStart + childX * scaleFactor, 10);
                  child.width = toFixedX(child.width * scaleFactor, 10);
                });
              }

              page.sections = sections;
              const changedSection = Section.fromDict(section).clone();
              if (section.children) changedSection.children = section.children;
              changedSection.setWidth(section.width);
              dispatch(editSection(changedSection.toDict()));
              if (section.id === firstSelSection.id) {
                firstSelSection.x = section.x;
                firstSelSection.width = section.width;
              }
            });
          page.sections = sections;
          dispatch(createSnapshotThunk());
        }
      }
      setTimeout(() => {
        page.sections = sections;
        if (page?.uid?.length) {
          dispatch(checkPageContentLength(page.uid));
        }
      }, 10);
    }
  }
};

export const appendMatrixCell = (position, type) => (dispatch, getState) => {
  const {
    core: {
      sectionsSelection,
      pages,
      pagesSelection,
      isMultiColored,
      paperColor,
      ribbonColor,
    },
  } = getState();
  const firstSelSection = getFirstSelectedSection(sectionsSelection, pages);
  const selSec = JSON.parse(JSON.stringify(firstSelSection));

  if (pagesSelection.length && pages[pagesSelection[0]] && firstSelSection) {
    const page = pages[pagesSelection[0]];
    const { layouts } = page;
    const shield = getShield(layouts, firstSelSection.shieldId);
    const shieldSections = getShieldSectionsArray(shield, page.sections);

    if (shield && shieldSections && layouts) {
      const { text_height, transforms } = getAllLayers(
        shield.id,
        pages
      ).group.textStyle;
      const matrixRotation =
        transforms?.angle === 90 || transforms?.angle === 270;

      const targetArea = getTargetArea(
        selSec,
        Object.values(shieldSections),
        matrixRotation
      );
      const x = matrixRotation ? 'y' : 'x';
      const y = matrixRotation ? 'x' : 'y';
      const width = matrixRotation ? 'height' : 'width';
      const height = matrixRotation ? 'width' : 'height';
      const yy = matrixRotation ? 'xx' : 'yy';

      const selSecBounds = getSectionBounds(selSec, 9);
      const minY = shieldSections.sort((a, b) => a[y] - b[y])[0][y];
      const mY = shieldSections.sort(
        (a, b) => b[y] + b[height] - (a[y] + a[height])
      )[0];
      const maxY = toFixedX(mY[y] + mY[height], 9);
      const targetWidth = targetArea.r - targetArea.l;
      const targetAreaCells = shieldSections.filter(
        s =>
          toFixedX(s[x], 5) >= toFixedX(targetArea.l, 5) &&
          toFixedX(s[x] + s[width], 5) <= toFixedX(targetArea.r, 5)
      );
      const minX = targetAreaCells.sort((a, b) => a[x] - b[x])[0][x];

      const afterAreaCells = shieldSections.filter(
        s => toFixedX(s[x], 5) >= toFixedX(targetArea.r, 5)
      );
      const selRowColumns = targetAreaCells
        .filter(s => toFixedX(s[y], 5) === toFixedX(selSec[y], 5))
        .sort((a, b) => a - b);
      const notSelRowColumns = targetAreaCells
        .filter(s => toFixedX(s[y], 5) !== toFixedX(selSec[y], 5))
        .sort((a, b) => a - b);
      const isSelSecBottomRow =
        toFixedX(selSecBounds[matrixRotation ? y : yy], 5) ===
        toFixedX(matrixRotation ? minY : maxY, 5);

      const newSec = createSectionSmart({
        sourceShield: shield,
        pages,
        ribbonColor: !isMultiColored && ribbonColor,
        paperColor: null,
      });
      if (newSec) {
        dispatch(createSnapshotThunk());
        const placeToAdd = { x: 0, y: 0 };
        const factorX =
          shield.isMatrixFixedCellWidth && isSelSecBottomRow
            ? 1
            : targetWidth / (targetWidth + selSec[width]);
        const selSecOffSetX = selSec[x] - minX;
        selSec[x] = toFixedX(minX + selSecOffSetX * factorX, 10);
        selSec[width] = toFixedX(selSec[width] * factorX, 10);

        const revert = transforms?.angle === 180 || transforms?.angle === 270;
        placeToAdd[x] =
          (revert && position === 'right') || (!revert && position === 'left')
            ? selSec[x]
            : toFixedX(selSec[x] + selSec[width], 10);
        placeToAdd[y] = selSec[y];

        selRowColumns.forEach(cell => {
          const cellOffSetX = cell[x] - minX;
          cell[x] = toFixedX(minX + cellOffSetX * factorX, 10);
          cell[width] = toFixedX(cell[width] * factorX, 10);
          cell[x] +=
            toFixedX(cell[x], 5) >= toFixedX(placeToAdd[x], 5)
              ? selSec[width]
              : 0;

          if (cell.children) {
            cell.children.forEach(child => {
              const childOffSetX = child[x] - minX;
              child[x] = toFixedX(minX + childOffSetX * factorX, 10);
              child[width] = toFixedX(child[width] * factorX, 10);
              child[x] +=
                toFixedX(child[x], 5) >= toFixedX(placeToAdd[x], 5)
                  ? selSec[width]
                  : 0;
            });
          }
        });

        if (factorX === 1) {
          const expand = (targetWidth + selSec[width]) / targetWidth;
          notSelRowColumns.forEach(cell => {
            const cellOffSetX = cell[x] - minX;
            cell[x] = toFixedX(minX + cellOffSetX * expand, 10);
            cell[width] = toFixedX(cell[width] * expand, 10);
            if (cell.children) {
              cell.children.forEach(child => {
                const childOffSetX = child[x] - minX;
                child[x] = toFixedX(minX + childOffSetX * expand, 10);
                child[width] = toFixedX(child[width] * expand, 10);
              });
            }
          });
          if (afterAreaCells?.length) {
            afterAreaCells.forEach(cell => {
              if (toFixedX(cell[x], 5) >= toFixedX(placeToAdd[x], 5)) {
                cell[x] += selSec[width];
                if (cell.children) {
                  cell.children.forEach(child => {
                    child[x] += selSec[width];
                  });
                }
              }
            });
          }
        }

        newSec.setX(placeToAdd.x);
        newSec.setY(placeToAdd.y);
        newSec.setWidth(selSec.width);
        newSec.setHeight(selSec.height);
        newSec.setFontSize(selSec.fontSize);
        newSec.setChanged(true);
        newSec.setText('');
        newSec.setType(type);
        newSec.setNewIdById(selSec.id);

        newSec.setBold(selSec.isBold);
        newSec.setItalic(selSec.isItalic);
        newSec.setUnderline(selSec.isUnderline);
        newSec.setFont(selSec.font);
        newSec.setHorizontalAlignment(selSec.horizontalAlignment);
        newSec.setVerticalAlignment(selSec.verticalAlignment);

        dispatch(addSectionAction(newSec.toDict ? newSec.toDict() : newSec));
        dispatch(adaptMatrixFontSize(shield, newSec[height], targetArea));
      }
      dispatch(clearSectionsSelection());
      setTimeout(() => {
        dispatch(handleSectionClick({ section: newSec }));
      }, 0);
      if (page?.uid?.length) {
        dispatch(checkPageContentLength(page.uid));
      }
    }
  }
};

export const addNewMatrix = data => (dispatch, getState) => {
  const matfileTemplate = getBuildedMatfileTemplate();
  const {
    core: { pages, pagesSelection, sectionsSelection },
  } = getState();
  let page = pages[pagesSelection[0]];

  if (
    matfileTemplate.type === MATFILE_ENDLESS_TYPE &&
    data.isMatrixFixedCellWidth
  ) {
    page.resizeType = ENDLESS_BASED_ON_CONTENT;
  }

  let selectedShield = null;
  const { layouts } = page;
  const firstSelSection = getFirstSelectedSection(sectionsSelection, pages);
  selectedShield = firstSelSection
    ? getShield(layouts, firstSelSection.shieldId)
    : null;

  if (!selectedShield) {
    let firstLayout = null;
    for (const layoutId in page.layouts) {
      firstLayout = page.layouts[layoutId];
      break;
    }
    let firstGroup = null;
    for (const groupId in firstLayout.groups) {
      firstGroup = firstLayout.groups[groupId];
      break;
    }
    for (const shieldId in firstGroup.shields) {
      selectedShield = firstGroup.shields[shieldId];
      break;
    }
  }

  if (selectedShield && data?.matrixRows) {
    const {
      text_height,
      font_name,
      bold,
      italic,
      underline,
      color,
      transforms,
    } = getAllLayers(selectedShield.id, pages).group.textStyle;
    const matrixRotation =
      transforms?.angle === 90 || transforms?.angle === 270;
    const h = matrixRotation ? 'w' : 'h';
    const cellFormat = {
      font: font_name,
      isBold: bold,
      isItalic: italic,
      isUnderline: underline,
      fontColor: color,
    };

    dispatch(
      setFontCoef((text_height / selectedShield.primitiveProps[h]).toFixed(2))
    );
    dispatch(createSnapshotThunk());
    dispatch(clearSectionsSelection());
    dispatch(clearShield(selectedShield.id));

    selectedShield.isMatrix = true; // must be after clearShield, clearShield sets isMatrix = false
    selectedShield.isMatrixFixedCellWidth = data.isMatrixFixedCellWidth;
    selectedShield.isMatrixBordersEnabled = data.isMatrixBordersEnabled;
    selectedShield.matrixCellWidth = data.matrixCellWidth;
    selectedShield.matrices = [];

    page.sections = {};

    if (page && selectedShield) {
      setTimeout(() => {
        let maxContentX = selectedShield.primitiveProps.x;
        const shieldSections = getShieldSectionsArray(
          selectedShield,
          page.sections
        );
        shieldSections.forEach(section => {
          const sectionRight = toFixedX(section.x + section.width, 10);
          maxContentX =
            maxContentX <= sectionRight ? sectionRight : maxContentX;
        });
        const offsetLeft = 0;

        selectedShield.isMatrix = true; // must be after clearShield, clearShield sets isMatrix = false
        selectedShield.isMatrixFixedCellWidth = data.isMatrixFixedCellWidth;
        selectedShield.isMatrixBordersEnabled = data.isMatrixBordersEnabled;
        selectedShield.matrixCellWidth = data.matrixCellWidth;
        selectedShield.matrices = selectedShield.matrices
          ? selectedShield.matrices
          : [];
        selectedShield.matrices.push(data);

        const { layouts } = page;
        for (let layoutId in layouts) {
          const { groups } = layouts[layoutId];
          for (let groupId in groups) {
            const { shields } = groups[groupId];
            for (let shId in shields) {
              const shield = shields[shId];
              if (shield.id === selectedShield.id) {
                shield.isMatrix = selectedShield.isMatrix;
                shield.matrixCellWidth = selectedShield.matrixCellWidth;
                shield.isMatrixFixedCellWidth =
                  selectedShield.isMatrixFixedCellWidth;
                shield.isMatrixBordersEnabled =
                  selectedShield.isMatrixBordersEnabled;
                shield.matrices = selectedShield.matrices;
                selectedShield.primitiveProps = shield.primitiveProps;
                break;
              }
            }
          }
        }

        const rows = data.matrixRows.length ? data.matrixRows.length : 1;
        const cellHeight = matrixRotation
          ? toFixedX(selectedShield.primitiveProps.w / rows, 10)
          : toFixedX(selectedShield.primitiveProps.h / rows, 10);
        let maxColumns = 0;
        let firstMaxColumnRow = 0;
        data.matrixRows.forEach((row, index) => {
          firstMaxColumnRow =
            maxColumns < row.cells?.length ? index : firstMaxColumnRow;
          maxColumns =
            maxColumns < row.cells?.length ? row.cells.length : maxColumns;
        });

        const separatorWidth = data.isMatrixSeparator
          ? data.matrixSeparatorWidth
          : 0;
        const maxRowWidth = data.isMatrixFixedCellWidth
          ? maxColumns * data.matrixCellWidth
          : matrixRotation
          ? selectedShield.primitiveProps.h - separatorWidth
          : selectedShield.primitiveProps.w - separatorWidth;

        //important part, override pages in state at first
        const {
          core: { pages, pagesOrder },
        } = getState();

        pages[page.uid].layouts = layouts;

        dispatch(overridePages(pages, pagesOrder, false));

        //fill page with sections
        let maxX = 0;
        for (let r = 0; r < data.matrixRows.length; r++) {
          const row = data.matrixRows[r];
          if (row.cells?.length > 0) {
            const cellWidth = toFixedX(maxRowWidth / row.cells.length, 10);
            const revert =
              transforms?.angle === 90 || transforms?.angle === 180;
            const row_num = revert ? data.matrixRows.length - 1 - r : r;

            if (matrixRotation) {
              for (let c = 0; c < row.cells.length; c++) {
                const x = toFixedX(
                  row_num * cellHeight + selectedShield.primitiveProps.x,
                  10
                );
                const y =
                  offsetLeft +
                  toFixedX(c * cellWidth + selectedShield.primitiveProps.y, 10);
                const text = ''; //"R" + r + " C" + c;
                maxX =
                  maxX < toFixedX(x + cellWidth, 10)
                    ? toFixedX(x + cellWidth, 10)
                    : maxX;
                const setSelected = r === firstMaxColumnRow && c === 0;
                dispatch(
                  appendMatrixSectionAtShield({
                    shield: selectedShield,
                    x,
                    y,
                    cellWidth: cellHeight,
                    cellHeight: cellWidth,
                    text,
                    children: false,
                    isSeparator: false,
                    setSelectd: setSelected,
                    cellFormat,
                  })
                );
              }
            } else {
              for (let c = 0; c < row.cells.length; c++) {
                const x =
                  offsetLeft +
                  toFixedX(c * cellWidth + selectedShield.primitiveProps.x, 10);
                const y = toFixedX(
                  row_num * cellHeight + selectedShield.primitiveProps.y,
                  10
                );
                const text = '';
                maxX =
                  maxX < toFixedX(x + cellWidth, 10)
                    ? toFixedX(x + cellWidth, 10)
                    : maxX;
                const setSelected = r === firstMaxColumnRow && c === 0;
                dispatch(
                  appendMatrixSectionAtShield({
                    shield: selectedShield,
                    x,
                    y,
                    cellWidth,
                    cellHeight,
                    text,
                    children: false,
                    isSeparator: false,
                    setSelectd: setSelected,
                    cellFormat,
                  })
                );
              }
            }
          }
        }

        if (separatorWidth > 0) {
          dispatch(
            appendMatrixSectionAtShield({
              shield: selectedShield,
              x: maxX,
              y: selectedShield.primitiveProps.y,
              cellWidth: separatorWidth,
              cellHeight: selectedShield.primitiveProps.h,
              text: '',
              children: null,
              isSeparator: true,
              setSelectd: false,
              cellFormat,
            })
          );
        }
        if (page?.uid?.length) {
          dispatch(checkPageContentLength(page.uid));
        }
      }, 0);
    }
  }
};

export const appendMatrixSectionAtShield = payload => (dispatch, getState) => {
  const {
    shield,
    x,
    y,
    cellWidth,
    cellHeight,
    text,
    code = null,
    glyph = null,
    image = null,
    shape = null,
    children,
    isSeparator,
    setSelectd,
    cellFormat,
  } = payload;

  if (shield) {
    const {
      core: { pages, isMultiColored, paperColor, ribbonColor, fontCoef },
    } = getState();
    const layers = getAllLayers(shield.id, pages);
    const sections = Object.values(layers.page.sections);
    const avgFontSize =
      sections.reduce((acc, item) => (acc += item.fontSize), 0) /
      sections.length;
    const isContentEmpty = Object.values(layers.page.sections).every(
      item => !item.text
    );
    const section = createSectionSmart({
      sourceShield: shield,
      pages,
      ribbonColor: !isMultiColored && ribbonColor,
      paperColor: null,
    });

    if (section) {
      const { text_height, transforms } = getAllLayers(
        shield.id,
        pages
      ).group.textStyle;
      const matrixRotation =
        transforms?.angle === 90 || transforms?.angle === 270;
      const h = matrixRotation ? 'w' : 'h';
      const width = matrixRotation ? 'height' : 'width';
      const defaultFontSize =
        text_height -
        (
          (shield.primitiveProps[h] /
            (matrixRotation ? cellWidth : cellHeight)) *
          fontCoef
        ).toFixed(1);
      const fontSize = cellFormat?.fontSize
        ? cellFormat.fontSize
        : isContentEmpty
        ? defaultFontSize
        : avgFontSize;

      section.setX(x);
      section.setY(y);
      section.setWidth(cellWidth);
      section.setHeight(cellHeight);
      section.setFontSize(fontSize);
      section.setChanged(true);
      section.setText(text);
      section.setCode(code);
      section.setGlyph(glyph);
      section.setImage(image);
      section.setShape(shape);

      section.setBold(cellFormat?.isBold === true);
      section.setItalic(cellFormat?.isItalic === true);
      section.setUnderline(cellFormat?.isUnderline === true);

      if (cellFormat?.font) section.setFont(cellFormat.font);
      if (cellFormat?.horizontalAlignment)
        section.setHorizontalAlignment(cellFormat.horizontalAlignment);
      if (cellFormat?.verticalAlignment)
        section.setVerticalAlignment(cellFormat.verticalAlignment);

      const _font = generateFontString(
        section.isBold,
        section.isItalic,
        fontSize,
        section.font
      );
      const w_width = measureTextWidth('W', _font);
      if (section[width] <= w_width) {
        const _fontSize = toFixedX(
          (fontSize * (section[width] * 0.8)) / w_width,
          1
        );
        section.setFontSize(_fontSize);
      }

      if (isSeparator) {
        section.setType('separator');
      }
      if (children?.length) section.setChildren(children);
      dispatch(addSectionAction(section.toDict ? section.toDict() : section));
      if (setSelectd) {
        setTimeout(() => {
          dispatch(addSectionToMatrixSelection(section));
        }, 0);
      }
    }
  }
};

export const mergeMatrixCells = () => (dispatch, getState) => {
  const {
    core: { sectionsSelection, pages, pagesSelection },
  } = getState();
  const firstSelSection = getFirstSelectedSection(sectionsSelection, pages);

  if (pagesSelection.length && pages[pagesSelection[0]] && firstSelSection) {
    const page = pages[pagesSelection[0]];
    const { layouts } = page;
    const shield = getShield(layouts, firstSelSection.shieldId);

    const { transforms } = getAllLayers(shield.id, pages).group.textStyle;
    const matrixRotation =
      transforms?.angle === 90 || transforms?.angle === 270;
    const x = matrixRotation ? 'y' : 'x';

    if (Object.keys(sectionsSelection).length && page.sections && layouts) {
      let right = firstSelSection.x;
      let left = firstSelSection.x + firstSelSection.width;
      let top = firstSelSection.y;
      let bottom = firstSelSection.y + firstSelSection.height;
      let text = firstSelSection.text;
      let code = firstSelSection.code;
      let glyph = firstSelSection.glyph;
      let image = firstSelSection.image;
      let shape = firstSelSection.shape;

      let selSec = JSON.parse(JSON.stringify(firstSelSection));
      let cellFormat = {
        fontSize: selSec.fontSize,
        isBold: selSec.isBold,
        isItalic: selSec.isItalic,
        isUnderline: selSec.isUnderline,
        font: selSec.font,
        horizontalAlignment: selSec.horizontalAlignment,
        verticalAlignment: selSec.verticalAlignment,
        fontColor: selSec.fontColor,
      };

      for (const sectionId in sectionsSelection) {
        const section = page.sections[sectionId];
        left = section.x < left ? section.x : left;
        right =
          section.x + section.width > right ? section.x + section.width : right;
        top = section.y < top ? section.y : top;
        bottom =
          section.y + section.height > bottom
            ? section.y + section.height
            : bottom;
        if (page.sections[sectionId][x] < selSec[x]) {
          selSec = JSON.parse(JSON.stringify(page.sections[sectionId]));
          text = selSec.text;
          code = selSec.code;
          glyph = selSec.glyph;
          image = selSec.image;
          shape = selSec.shape;
          cellFormat = getCellFormat(selSec);
        }
      }
      const width = toFixedX(right - left, 10);
      const height = toFixedX(bottom - top, 10);

      if (shield) {
        dispatch(createSnapshotThunk());
        const children = [];
        for (const sectionId in sectionsSelection) {
          const child = JSON.parse(JSON.stringify(page.sections[sectionId]));
          if (child.children?.length) {
            child.children.forEach(el => {
              children.push(el);
            });
          } else {
            children.push(child);
          }
          delete page.sections[sectionId];
          delete sectionsSelection[sectionId];
        }
        dispatch(clearSectionsSelection());
        dispatch(
          appendMatrixSectionAtShield({
            shield,
            x: left,
            y: top,
            cellWidth: width,
            cellHeight: height,
            text,
            code,
            glyph,
            image,
            shape,
            children,
            isSeparator: false,
            setSelectd: true,
            cellFormat,
          })
        );
      }
    }
  }
};

export const splitMatrixCell = () => (dispatch, getState) => {
  const {
    core: { sectionsSelection, pages, pagesSelection },
  } = getState();
  const currentPage = pages[pagesSelection[0]];
  const firstSelSection = getFirstSelectedSection(sectionsSelection, pages);
  const children = firstSelSection?.children?.length
    ? JSON.parse(JSON.stringify(firstSelSection.children))
    : null;

  if (!!children?.length) {
    const { layouts } = currentPage;
    const shield = getShield(layouts, firstSelSection.shieldId);

    if (shield) {
      dispatch(createSnapshotThunk());

      const width = Math.round((firstSelSection.width / children.length) * 1000) / 1000;
      const unmergedSectionList = children.map((sec) => ({ ...sec, width }));

      let x = firstSelSection.x;
      for (const s of unmergedSectionList) {
        s.x = x;
        x += s.width;
      }

      const unmergedSections = unmergedSectionList.reduce((acc, sec) => ({ ...acc, [sec.id]: sec }), {});
      const pageSections = Object.values(currentPage.sections).reduce((acc, sec) => {
        if (sec.id !== firstSelSection.id) {
          return { ...acc, [sec.id]: sec };
        }
        return acc;
      }, { ...unmergedSections });

      dispatch(
        overridePageAction({
          page: {
            ...currentPage,
            sections: pageSections,
          },
          sectionsSelection: unmergedSections,
          fictiveSections: {}
        })
      );
    }
  }
};

export const appendSectionAtShield = ({
  shield,
  isUndoable = false,
  setSelected = false,
}) => (dispatch, getState) => {
  if (shield) {
    const {
      core: { pages, isMultiColored, ribbonColor },
    } = getState();
    const section = createSectionSmart({
      sourceShield: shield,
      pages,
      ribbonColor: !isMultiColored && ribbonColor,
      paperColor: null,
    });
    if (section) {
      dispatch(addSection(section, isUndoable));
      if (setSelected) {
        dispatch(clearSectionsSelection());
        setTimeout(() => {
          dispatch(handleSectionClick({ section }));
        }, 0);
      }
    }
  }
};

export const handleBulkShapeSelection = shields =>
  createAction(BULK_SHAPE_SELECTION, () => shields)();

// not using now
export const eraseDeletedSection = () => dispatch => {
  // TODO: Deal with deleting sections properly
  dispatch(createAction(DELETE_SECTION, () => null)());
};

export const deleteSelectedSections = () => (dispatch, getState) => {
  const {
    core: { pages, pagesOrder, paginationPageNum, sectionsSelection },
  } = getState();
  const visiblePagesUids = getPaginationPageItemsByPage(
    pagesOrder,
    paginationPageNum
  );

  visiblePagesUids.forEach(pageId => {
    const page = pages[pageId];
    if (page?.sections) {
      dispatch(createSnapshotThunk());
      const preselected = JSON.parse(JSON.stringify(sectionsSelection));
      Object.values(preselected).forEach(selSec => {
        if (page.sections[selSec.id]) {
          dispatch(deleteSectionById(selSec.id, false));
        }
      });
      dispatch(handleFreeSpaceClick(true));
    }
  });
};

export const deleteSelectedContents = () => (dispatch, getState) => {
  const {
    core: {
      pages,
      pagesOrder,
      paginationPageNum,
      shieldsSelection,
      sectionsSelection,
    },
  } = getState();
  const visiblePagesUids = getPaginationPageItemsByPage(
    pagesOrder,
    paginationPageNum
  );

  visiblePagesUids.forEach(pageId => {
    const page = pages[pageId];
    const areShieldsSelected = Boolean(Object.keys(shieldsSelection).length);
    const selectedShieldsIds = Object.keys(shieldsSelection);
    if (areShieldsSelected) {
      dispatch(createSnapshotThunk());
      selectedShieldsIds.forEach(id => {
        const entries = Object.entries(page.sections);
        entries.forEach(([key, value]) => {
          if (value.shieldId === id && sectionsSelection[key]) {
            dispatch(setTextInSection(key, '', false));
          }
        });
        dispatch(handleFreeSpaceClick(true));
      });
    } else {
      dispatch(createSnapshotThunk());
      const entries = Object.entries(page.sections);
      entries.forEach(([key, value]) => {
        if (sectionsSelection[key]) {
          dispatch(setTextInSection(key, '', false));
        }
      });
      dispatch(handleFreeSpaceClick(true));
    }
  });
};

export const deleteWholeContent = () => (dispatch, getState) => {
  const {
    core: { pages, pagesOrder, paginationPageNum, shieldsSelection },
  } = getState();
  const visiblePagesUids = getPaginationPageItemsByPage(
    pagesOrder,
    paginationPageNum
  );

  visiblePagesUids.forEach(pageId => {
    const page = pages[pageId];
    const areShieldsSelected = Boolean(Object.keys(shieldsSelection).length);
    const selectedShieldsIds = Object.keys(shieldsSelection);
    if (areShieldsSelected) {
      dispatch(createSnapshotThunk());
      selectedShieldsIds.forEach(id => {
        const entries = Object.entries(page.sections);
        entries.forEach(([key, value]) => {
          if (value.shieldId === id) {
            dispatch(setTextInSection(key, '', false));
          }
        });
        dispatch(handleFreeSpaceClick(true));
      });
    } else {
      dispatch(createSnapshotThunk());
      const entries = Object.entries(page.sections);
      entries.forEach(([key, value]) => {
        dispatch(setTextInSection(key, '', false));
      });
      dispatch(handleFreeSpaceClick(true));
    }
  });
};

export const deleteAllSections = () => (dispatch, getState) => {
  const {
    core: { pages, pagesOrder, paginationPageNum, shieldsSelection },
  } = getState();
  const visiblePagesUids = getPaginationPageItemsByPage(
    pagesOrder,
    paginationPageNum
  );

  visiblePagesUids.forEach(pageId => {
    const page = pages[pageId];
    const areShieldsSelected = Boolean(Object.keys(shieldsSelection).length);
    const selectedShieldsIds = Object.keys(shieldsSelection);
    let selectedShield = null;
    for (const shieldId in shieldsSelection) {
      selectedShield = shieldsSelection[shieldId];
      selectedShield.isMatrix = false;
      selectedShield.matrices = null;
      break;
    }

    if (areShieldsSelected) {
      dispatch(createSnapshotThunk());
      selectedShieldsIds.forEach(id => {
        const entries = Object.entries(page.sections);
        entries.forEach(([key, value]) => {
          if (value.shieldId === id) {
            delete page.sections[key];
          }
        });
      });
      dispatch(deleteSelectedSectionsAction({ page }));
    } else {
      dispatch(createSnapshotThunk());
      const { layouts } = page;
      for (const layoutUid in layouts) {
        const { groups } = layouts[layoutUid];
        for (const groupUid in groups) {
          const { shields } = groups[groupUid];
          for (const shieldId in shields) {
            shields[shieldId].isMatrix = false;
            shields[shieldId].matrices = null;
          }
        }
      }

      dispatch(createAction(DELETE_SECTIONS, () => page)());
    }
  });
};

export const deleteSectionById = (sectionId, isUndoable = false) => (
  dispatch,
  getState
) => {
  if (sectionId) {
    const {
      core: { pages, selWizard },
    } = getState();
    const { page, shield, section: selectedSection } = getAllLayers(
      sectionId,
      pages
    );
    const shieldSections = getShieldSectionsArray(shield, page.sections);
    if (shieldSections.length > 0) {
      if (isUndoable) {
        dispatch(createSnapshotThunk());
      }
      if (shield.isMatrix && shieldSections?.length > 1) {
        // calculation for matrix only
        const selSec = JSON.parse(JSON.stringify(selectedSection));
        const { text_height, transforms } = getAllLayers(
          shield.id,
          pages
        ).group.textStyle;
        const matrixRotation =
          transforms?.angle === 90 || transforms?.angle === 270;
        // important: get targetArea before deleting
        const targetArea = getTargetArea(
          selSec,
          Object.values(shieldSections),
          matrixRotation
        );
        dispatch(createAction(DELETE_SECTION, () => selectedSection)());
        const x = matrixRotation ? 'y' : 'x';
        const y = matrixRotation ? 'x' : 'y';
        const yy = matrixRotation ? 'xx' : 'yy';
        const width = matrixRotation ? 'height' : 'width';
        const height = matrixRotation ? 'width' : 'height';
        const cellHeightBefore = selectedSection[height];

        const selSecBounds = getSectionBounds(selSec, 9);

        const minY = shieldSections.sort((a, b) => a[y] - b[y])[0][y];
        const mY = shieldSections.sort(
          (a, b) => b[y] + b[height] - (a[y] + a[height])
        )[0];
        const maxY = toFixedX(mY[y] + mY[height], 9);
        const minX = shieldSections.sort((a, b) => a[x] - b[x])[0][x];
        const mX = shieldSections.sort(
          (a, b) => b[x] + b[width] - (a[x] + a[width])
        )[0];
        const maxX = toFixedX(mX[x] + mX[width], 9);
        const totalWidth = toFixedX(maxX - minX);
        const totalHeight = toFixedX(maxY - minY);
        const targetWidth = targetArea.r - targetArea.l;
        const targetAreaCells = shieldSections.filter(
          s =>
            s.id !== selSec.id &&
            toFixedX(s[x], 5) >= toFixedX(targetArea.l, 5) &&
            toFixedX(s[x] + s[width], 5) <= toFixedX(targetArea.r, 5)
        );

        const afterAreaCells = shieldSections.filter(
          s => toFixedX(s[x], 5) >= toFixedX(targetArea.r, 5)
        );
        const rowsY = [...new Set(targetAreaCells.map(s => s[y]))].sort(
          (a, b) => a - b
        );
        const selRowColumns = targetAreaCells
          .filter(s => toFixedX(s[y], 5) === toFixedX(selSec[y], 5))
          .sort((a, b) => a - b);

        const shouldRemoveTargetRow = !selRowColumns?.length > 0;
        const isSelSecBottomRow =
          shouldRemoveTargetRow ||
          toFixedX(selSecBounds[matrixRotation ? y : yy], 5) ===
            toFixedX(matrixRotation ? minY : maxY, 5);
        let shouldMoveLeft = targetAreaCells?.length === 0;

        if (shouldRemoveTargetRow) {
          const newHeight = totalHeight - selSec[height];
          const factorHeight = newHeight > 0 ? totalHeight / newHeight : 1;
          targetAreaCells
            .sort((a, b) => a[y] - b[y])
            .forEach(cell => {
              if (cell[y] > selSec[y]) {
                cell[y] -= selSec[height];
                if (cell.children) {
                  cell.children.forEach(child => {
                    child[y] -= selSec[height];
                  });
                }
              }
              // resize cells rows after removing a row
              const sectionOffsetY = cell[y] - minY;
              cell[y] = toFixedX(minY + sectionOffsetY * factorHeight, 10);
              cell[height] = toFixedX(cell[height] * factorHeight, 10);
              if (cell.children) {
                cell.children.forEach(child => {
                  child[y] = toFixedX(minY + sectionOffsetY * factorHeight, 10);
                  child[height] = toFixedX(child[height] * factorHeight, 10);
                });
              }
              // here to calculate font size
              page.sections[cell.id] = cell;
            });
          if (selSec.type === 'separator') {
            shieldSections.forEach(cell => {
              if (
                selSec[x] >= cell[x] &&
                selSec[x] + selSec[width] <= cell[x] + cell[width]
              ) {
                cell[width] -= selSec[width];
                if (cell.children) {
                  cell.children.forEach(child => {
                    if (
                      selSec[x] >= child[x] &&
                      selSec[x] + selSec[width] <= child[x] + child[width]
                    ) {
                      child[width] -= selSec[width];
                    } else if (selSec[x] + selSec[width] <= child[x]) {
                      child[x] -= selSec[width];
                    }
                  });
                }
              }
            });
          }
        } else {
          const newWidth = targetWidth - selSec[width];
          const factorWidth = newWidth > 0 ? targetWidth / newWidth : 1;
          let start = targetArea.l;
          selRowColumns
            .sort((a, b) => a[x] - b[x])
            .forEach(cell => {
              cell[x] = toFixedX(start, 10);
              cell[width] = toFixedX(cell[width] * factorWidth, 10);
              if (cell.children) {
                cell.children.forEach(child => {
                  child[x] = toFixedX(start, 10);
                  child[width] = toFixedX(child[width] * factorWidth, 10);
                });
              }
              // here to calculate font size
              page.sections[cell.id] = cell;
              start += cell[width];
            });
          if (shield.isMatrixFixedCellWidth && isSelSecBottomRow) {
            shouldMoveLeft = true;
            const factorSmaller = targetWidth > 0 ? newWidth / targetWidth : 1;
            targetAreaCells
              .sort((a, b) => a[x] - b[x])
              .forEach(cell => {
                const sectionOffsetX = cell[x] - targetArea.l;
                cell[x] = toFixedX(
                  targetArea.l + sectionOffsetX * factorSmaller,
                  10
                );
                cell[width] = toFixedX(cell[width] * factorSmaller, 10);
                if (cell.children) {
                  cell.children.forEach(child => {
                    child[x] = toFixedX(
                      targetArea.l + sectionOffsetX * factorSmaller,
                      10
                    );
                    child[width] = toFixedX(child[width] * factorSmaller, 10);
                  });
                }
                // here to calculate font size
                page.sections[cell.id] = cell;
              });
          }
        }

        if (afterAreaCells?.length && shouldMoveLeft) {
          afterAreaCells.forEach(cell => {
            cell[x] = toFixedX(cell[x] - selSec[width], 10);
            if (cell.children) {
              cell.children.forEach(child => {
                child[x] = toFixedX(child[x] - selSec[width], 10);
              });
            }
            // here to calculate font size
            page.sections[cell.id] = cell;
          });
        }

        if (!shield.isMatrixFixedCellWidth && selSec.type === 'separator') {
          const factorSeparator =
            totalWidth > 0 ? totalWidth / (totalWidth - selSec[width]) : 1;
          shieldSections
            .sort((a, b) => a[x] - b[x])
            .forEach(cell => {
              const sectionOffsetX = cell[x] - minX;
              cell[x] = toFixedX(minX + sectionOffsetX * factorSeparator, 10);
              cell[width] = toFixedX(cell[width] * factorSeparator, 10);
              if (cell.children) {
                cell.children.forEach(child => {
                  const childOffsetX = child[x] - minX;
                  child[x] = toFixedX(
                    minX + childOffsetX * factorSeparator,
                    10
                  );
                  child[width] = toFixedX(child[width] * factorSeparator, 10);
                });
              }
              // here to calculate font size
              page.sections[cell.id] = cell;
            });
        }

        if (page?.uid?.length) {
          dispatch(checkPageContentLength(page.uid));
          if (selSec.type !== 'separator')
            dispatch(adaptMatrixFontSize(shield, cellHeightBefore, targetArea));
          dispatch(cleanSectionsAxes(shield));
        }
      } else {
        shield.isMatrix = null;
        shield.matrices = null;
        dispatch(createAction(DELETE_SECTION, () => selectedSection)());
      }
      if (page?.uid?.length) {
        dispatch(clearShieldsSelection());
        dispatch(checkPageContentLength(page.uid));
      }
      if (selWizard?.application_type === 'CABLE_FLAG') {
        dispatch(checkSyncSlaveShieldByMasterShield(shield));
      }
    }
  }
};

export const deleteSection = (isUndoable = false) => (dispatch, getState) => {
  const {
    core: { sectionsSelection },
  } = getState();
  if (isUndoable) {
    dispatch(createSnapshotThunk());
  }

  for (const sectionId in sectionsSelection) {
    dispatch(deleteSectionById(sectionId, isUndoable));
    break;
  }
};

export const clearShield = shieldId => (dispatch, getState) => {
  const {
    core: { pages, selWizard },
  } = getState();
  const {
    page: { sections },
    shield,
  } = getAllLayers(shieldId, pages);
  shield.isMatrix = false;
  shield.matrices = null;

  const shieldSections = getShieldSectionsArray(shield, sections);
  shieldSections.forEach(sec => {
    if (selWizard?.application_type === 'CABLE_FLAG') {
      dispatch(createAction(DELETE_SECTION, () => sec, false)());
    } else {
      dispatch(deleteSectionById(sec.id, false));
    }
  });
};

export const handleEndlessTextInput = (sectionId, isUndoable = true) => (
  dispatch,
  getState
) => {
  const {
    core: { pages, selWizard, scaleCoef },
  } = getState();
  const contentMargin = selWizard?.data?.isFlagLengthAuto ? 2 : 0;
  const { type: matfileType } = getBuildedMatfileTemplate();
  const { page, section } = getAllLayers(sectionId, pages);

  if (!page) {
    return;
  }

  const { resizeType, endless, uid: pageUid, layouts } = page;

  let selectedShield = null;
  if (layouts && section) {
    selectedShield = getShield(layouts, section.shieldId);
  }

  if (
    section &&
    selectedShield &&
    matfileType === MATFILE_ENDLESS_TYPE &&
    resizeType === ENDLESS_BASED_ON_CONTENT
  ) {
    const _endless = selectedShield?.isMatrix
      ? {
          length: {
            minimum: section.width,
            maximum: section.width,
          },
        }
      : endless;
    const {
      length: { minimum, maximum },
    } = _endless;
    const activeElement = getActiveElement();
    const textNode = getElementByQuerySelector(`#${sectionId}-text`);

    let newLength = minimum;
    let bBox = null;
    if (textNode) {
      bBox = textNode.getBBox();
    } else if (
      activeElement?.tagName &&
      activeElement.tagName.toUpperCase() === 'TEXTAREA'
    ) {
      bBox = {
        width:
          activeElement?.value?.length > 0
            ? activeElement.scrollWidth / scaleCoef
            : minimum - contentMargin,
        height: activeElement.scrollHeight / scaleCoef,
      };
    }
    if (bBox) {
      const width =
        section.angle === 0 || section.angle === 180 ? 'width' : 'height';
      if (bBox[width] + contentMargin <= minimum) {
        newLength = minimum;
      } else if (bBox[width] + contentMargin >= maximum) {
        newLength = maximum;
      } else {
        newLength = bBox[width] + contentMargin;
      }
    }
    newLength = selectedShield.isMatrix
      ? section.width
      : Math.round(newLength * 10) / 10;
    const changedSection = Section.fromDict(section)
      .clone()
      .setWidth(newLength);
    if (isUndoable) {
      dispatch(createSnapshotThunk());
    }
    dispatch(editSection(changedSection.toDict()));
    dispatch(checkPageContentLength(pageUid));
    setTimeout(() => {
      if (selWizard?.application_type === 'CABLE_FLAG') {
        dispatch(checkSyncSlaveShieldBySection(changedSection.toDict()));
        dispatch(handleSectionClick({ section: changedSection.toDict() }));
      }
    }, 0);
  }
};

export const resetState = () => dispatch => {
  dispatch(createAction(RESET_STATE, () => {})());
  dispatch(resetGlobalSettingsAction());
};

export const setTargetDevice = device =>
  createAction(SET_TARGET_DEVICE, () => device)();

export const setPreselectedPrinter = payload =>
  createAction(SET_PRESELECTED_PRINTER, () => payload)();

export const setPrinterList = payload =>
  createAction(SET_PRINTER_LIST, () => payload)();

export const setAppModeAction = payload =>
  createAction(SET_MODE, () => payload)();

export const setModeActionThunk = newMode => (dispatch, getState) => {
  const {
    core: { mode, selWizard },
  } = getState();

  dispatch(setAppModeAction(newMode));
  dispatch(setEditorMenuTab(null));
  dispatch(clearSectionsSelection());
  dispatch(clearShieldsSelection());
  dispatch(setCellSelectionType(SINGLE_CELLS));

  if (mode === INTERACTION_MODE.WIZARD && selWizard?.data?.isFlagLengthAuto) {
    setTimeout(() => {
      dispatch(setPageChildrenBasedOnContent());
    }, 10);
  }
};

export const clearCellSelection = () =>
  createAction(CLEAR_GROUP_SELECTION, () => {})();

export const setEditorTab = tab => dispatch => {
  dispatch(createAction(SET_EDITOR_TAB, () => tab)());
};

export const setCellSelectionType = selectionType => dispatch => {
  dispatch(createAction(SET_CELL_SELECTION_TYPE, () => selectionType)());
  dispatch(clearSectionsSelection());
  dispatch(clearShieldsSelection());
  dispatch(updateCellsSelection());
};

const updateCellsSelection = () => (dispatch, getState) => {
  const {
    core: { cellSelectionType, pages, groupSelectionType },
  } = getState();
  if (cellSelectionType === SINGLE_CELLS) {
    const shields = {};
    for (const pageId in pages) {
      const page = pages[pageId];
      for (const layoutId in page.layouts) {
        const layout = page.layouts[layoutId];
        for (const groupId in layout.groups) {
          const group = layout.groups[groupId];
          if (groupSelectionType.some(el => el === group.id)) {
            for (const shieldId in group.shields) {
              const shield = group.shields[shieldId];
              shields[shieldId] = shield;
            }
          }
        }
      }
    }
    dispatch(handleBulkShapeSelection(shields));
  }
};

const setGroupSelectionType = payload => {
  return createAction(SET_GROUP_SELECTION_TYPE, () => payload)();
};

export const setGroupSelectionTypeThunk = selectionType => (
  dispatch,
  getState
) => {
  const {
    core: {
      pages,
      pagesSelection,
      groupSelectionType
    },
  } = getState();
  const currentPage = pages[pagesSelection[0]];
  const newGroupSelectionType = groupSelectionType.includes(selectionType)
    ? groupSelectionType.filter(groupId => groupId !== selectionType)
    : [...groupSelectionType, selectionType];
  const shieldsSelection = newGroupSelectionType.reduce((acc, groupId) => {
    const shields = getShieldsByGroupId({ id: groupId, page: currentPage });
    return { ...acc, ...shields };
  }, {});
  const sectionsSelection = getSectionsByShields(shieldsSelection);

  dispatch(
    setGroupSelectionType({
      groupSelectionType: newGroupSelectionType,
      sectionsSelection,
      shieldsSelection,
    })
  );
};

export const setEditorMenuTab = editorMenuTab =>
  createAction(SET_EDITOR_MENU_TAB, () => editorMenuTab)();

export const clearShieldsSelection = () => {
  return createAction(CLEAR_SHIELDS_SELECTION, () => null)();
};

export const clearSectionsSelection = () => {
  return createAction(CLEAR_SECTIONS_SELECTION, () => null)();
};

export const switchSelectedShield = shape => dispatch => {
  dispatch(createAction(SWITCH_SELECTED_SHIELD, () => shape)());
};

export const removeSelectedShield = shape => dispatch => {
  dispatch(createAction(REMOVE_SHIELD_FROM_SELECTION, () => shape)());
};

export const handleShapeClick = (shape, isUndoable = false) => (dispatch, getState) => {
  const {
    core: {
      pages,
      pagesSelection,
      ribbonColor
    },
  } = getState();
  const { logicalType } = shape;

  switch (logicalType) {
    case 'shield': {
      const currentPage = pages[pagesSelection[0]];
      const section = createSectionSmart({
        sourceShield: shape,
        pages,
        ribbonColor
      });
      const sectionsSelection = { [section.id]: section };
      const shieldsSelection = { [shape.id]: shape };
      const page = {
        ...currentPage,
        sections: {
          ...currentPage.sections,
          ...sectionsSelection,
        }
      };

      dispatch(createSnapshotThunk({ isUndoable }));
      dispatch(
        addNewSectionsAction({
          page,
          sectionsSelection,
          shieldsSelection,
          isUndoRedoStateChanged: isUndoable,
        })
      );
      break;
    }
    default: {
      break;
    }
  }
};

// TODO: check it. some strange behavior happens when uploaded project from api cmme is loaded to Editor
export const setSelectedPageFirstShieldSection = pageUid => (
  dispatch,
  getState
) => {
  const {
    core: { pages },
  } = getState();

  const firstPage = pages[pageUid];
  let firstLayout = null;
  for (const layoutId in firstPage.layouts) {
    firstLayout = firstPage.layouts[layoutId];
    break;
  }
  let firstGroup = null;
  for (const groupId in firstLayout.groups) {
    firstGroup = firstLayout.groups[groupId];
    break;
  }
  let shield = null;
  for (const shieldId in firstGroup.shields) {
    shield = firstGroup.shields[shieldId];
    break;
  }

  if (shield === null) {
    shield = getAllLayers(pages[pageUid].nearestPageShieldId, pages);
  }

  if (shield) {
    const shieldSections = getShieldSectionsArray(
      shield,
      pages[pageUid].sections
    );
    if (shieldSections.length === 0) {
      dispatch(handleShapeClick(shield));
    } else {
      let secToSel = null;
      if (shieldSections.length === 1) {
        secToSel = shieldSections[0];
      } else {
        // find section nearest to shield position coords
        let nearestSection = { obj: null, dist: Infinity };
        shieldSections.forEach(shSec => {
          const dist = Math.hypot(
            shSec.x - shield.primitiveProps.x,
            shSec.y - shield.primitiveProps.y
          );
          if (nearestSection.dist > dist) {
            nearestSection.dist = dist;
            nearestSection.obj = shSec;
          }
        });

        secToSel = nearestSection.obj;
      }
      dispatch(handleSectionClick({ section: secToSel }));
    }
  }
};

export const handleResizeBasedOnContent = () => (dispatch, getState) => {
  const {
    core: { pages, sectionsSelection },
  } = getState();
  let section;
  for (const secId in sectionsSelection) {
    const { section: sec } = getAllLayers(secId, pages);
    section = sec;
    break;
  }
  if (section?.text) {
    sectionsSelection[section.id].initText = section.text;
    dispatch(setTextInSection(section.id, section.text));
  }
};

export const changePageChildrenLength = (pageUid, includeEndlessResize) => (
  dispatch,
  getState
) => {
  const {
    core: { selWizard, pages },
  } = getState();

  if (selWizard?.application_type === 'CABLE_FLAG' && selWizard?.data) {
    if (selWizard?.data?.isFlagLengthAuto) {
      if (pages[pageUid].resizeType !== ENDLESS_BASED_ON_CONTENT) {
        dispatch(
          setPageResizeTypeAction({
            type: ENDLESS_BASED_ON_CONTENT,
            pageId: pageUid,
          })
        );
      }
    } else {
      if (pages[pageUid].resizeType !== ENDLESS_FIXED) {
        dispatch(
          setPageResizeTypeAction({ type: ENDLESS_FIXED, pageId: pageUid })
        );
      }
    }

    const flagMidLength = Math.ceil(selWizard.data.flagMidLength);
    const flagLength = Math.ceil(selWizard.data.flagLength * 10) / 10;
    const initWizardLength = flagLength * 2 + flagMidLength;

    dispatch(setPageLength(initWizardLength, pageUid));
    recalcZoomBordersScaleCoefs(initWizardLength);
    dispatch(handleZoomStateChange(ZOOM_OUT_STATE));
    dispatch(handleZoomClick());
  }
};

export const setPageChildrenBasedOnContent = (
  _pageId,
  isManuallyChanged = false
) => (dispatch, getState) => {
  const {
    core: {
      pages,
      pagesSelection,
      sectionsSelection,
      shieldsSelection,
      selWizard,
    },
  } = getState();
  let selectedShield = null;
  for (const shieldId in shieldsSelection) {
    selectedShield = shieldsSelection[shieldId];
    break;
  }

  let pageId = _pageId;
  if (!pageId) {
    const page = pages[pagesSelection[0]];
    pageId = page.uid;
  }

  if (!selectedShield?.isMatrix) {
    let section;
    for (const secId in sectionsSelection) {
      const { section: sec } = getAllLayers(secId, pages);
      section = sec;
      break;
    }

    if (
      !section ||
      (pageId &&
        selWizard?.application_type === 'CABLE_FLAG' &&
        selWizard?.data?.isFlagLengthAuto)
    ) {
      //only without selected section
      if (pageId) {
        const page = pages[pageId];
        if (page?.sections) {
          for (const secId in page.sections) {
            if (
              secId.length > 0 &&
              (page.sections[secId].isChanged || isManuallyChanged)
            ) {
              dispatch(handleEndlessTextInput(secId, false));
            }
          }
        }
        dispatch(checkPageContentLength(pageId));
      }
    }
  }
};

export const setFictiveSections = sections => dispatch => {
  dispatch(
    createAction(SET_FICTIVE_SECTIONS, () => (sections ? sections : {}))()
  );
};

export const addSectionToMatrixSelection = section => dispatch => {
  dispatch(
    createAction(ADD_SECTION_TO_MATRIX_SELECTION, () =>
      section.toDict ? section.toDict() : section
    )()
  );
};

export const removeSectionFromMatrixSelection = section => dispatch => {
  dispatch(
    createAction(REMOVE_SECTION_FROM_MATRIX_SELECTION, () =>
      section.toDict ? section.toDict() : section
    )()
  );
};

export const setAutofillMatrixTemplate = (payload) => {
  return createAction(SET_AUTOFILL_MATRIX_TEMPLATE, () => payload)();
};

export const switchSelectedSection = section => dispatch => {
  dispatch(
    createAction(SWITCH_SELECTED_SECTION, () =>
      section.toDict ? section.toDict() : section
    )()
  );
};

export const setRemoveBlurFromAllInputs = value => dispatch => {
  if (value) {
    getElementsByQuerySelectorAll('input').forEach(el => el.blur());
  }

  dispatch(createAction(REMOVE_BLUR_FROM_ALL_INPUTS, () => value)());
};

export const handleSectionClick = payload => (dispatch, getState) => {
  const {
    section,
    isCtrlPressed = false,
    floatableInputVisible = false,
  } = payload;
  const {
    core: { mode, sectionsSelection, pages },
  } = getState();
  const layers = getAllLayers(section.id, pages);
  const { shield: parentShield, section: currentSection } = layers;

  if (isCtrlPressed) {
    dispatch(addSelectedSectionThunk(currentSection));
  } else {
    switch (mode) {
      case INTERACTION_MODE.EDIT: {
        if (parentShield?.isMatrix) {
          if (sectionsSelection[currentSection.id]) {
            dispatch(removeSectionFromMatrixSelection(currentSection));
          } else {
            dispatch(clearSectionsSelection());
            dispatch(addSectionToMatrixSelection(currentSection));
          }
        } else {
          dispatch(
            applyGlobalSectionSettingsToSection({
              section: currentSection,
              shield: parentShield,
            })
          );
          dispatch(
            addSelectedSections({
              sectionsSelection: { [currentSection.id]: currentSection },
              shieldsSelection: { [parentShield.id]: parentShield },
              floatableInputVisible,
            })
          );
          break;
        }
        break;
      }
      default: {
        break;
      }
    }
  }
};

export const handleFreeSpaceClick = () => (dispatch, getState) => {
  const {
    core: { mode, shieldsSelection, sectionsSelection },
  } = getState();

  switch (mode) {
    case INTERACTION_MODE.EDIT: {
      if (Object.keys(sectionsSelection).length) {
        dispatch(clearSectionsSelection());
      }
      if (Object.keys(shieldsSelection).length) {
        dispatch(clearShieldsSelection());
      }
      dispatch(setEditorMenuTab(null));
      break;
    }
    default: {
      break;
    }
  }
};

export const setMultiColoredState = isMultiColored =>
  createAction(SET_MULTI_COLORED_STATE, () => isMultiColored)();

export const setColoredState = isColored =>
  createAction(SET_COLORED_STATE, () => isColored)();

export const setSectionsDisplayState = isHidden => {
  return createAction(SET_SECTIONS_DISPLAY_STATE, () => isHidden)();
};

export const setGridDisplayState = isDisplayed =>
  createAction(SET_GRID_DISPLAY_STATE, () => isDisplayed)();

export const setGridWidth = width =>
  createAction(SET_GRID_WIDTH, () => width)();

export const setInputDisplayState = isShown =>
  createAction(SET_INPUT_DISPLAY_STATE, () => isShown)();

export const setTextTypingResource = res => {
  return createAction(SET_TEXT_TYPING_RESOURCE, () => res)();
};

export const setUserDeviceManipulatorType = manipulatorType =>
  createAction(SET_USER_DEVICE_MANIPULATOR_TYPE, () => manipulatorType)();

export const addSection = (section, isUndoable = false) => dispatch => {
  if (isUndoable) {
    dispatch(createSnapshotThunk());
  }
  dispatch(addSectionAction(section.toDict ? section.toDict() : section));
};

export const removeSectionFromSelection = section => {
  return createAction(REMOVE_SECTION_FROM_SELECTION, () => section.id)();
};

export const handleWindowResize = event => (dispatch, getState) => {
  recalcZoomBordersScaleCoefs();
};

export const handleWindowMouseMove = event => (dispatch, getState) => {
  const {
    core: { userDeviceManipulatorType },
  } = getState();

  // can be fired for touch(click), so need to exclude this situation
  if (!!event.movementY || !!event.movementX) {
    if (userDeviceManipulatorType !== USER_DEVICE_MANIPULATOR_TYPE.MOUSE) {
      dispatch(
        setUserDeviceManipulatorType(USER_DEVICE_MANIPULATOR_TYPE.MOUSE)
      );
    }
  }
};

export const handleWindowTouchStart = event => (dispatch, getState) => {
  const {
    core: { userDeviceManipulatorType },
  } = getState();

  if (userDeviceManipulatorType !== USER_DEVICE_MANIPULATOR_TYPE.TOUCH) {
    dispatch(setUserDeviceManipulatorType(USER_DEVICE_MANIPULATOR_TYPE.TOUCH));
  }
};

export const setAutoFillAndCopyPattern = pattern =>
  createAction(SET_AUTOFILL_AND_COPPY_PATTERN, () => pattern)();

export const setFontCoef = coef => createAction(SET_FONT_COEF, () => coef)();

export const adaptMatrixFontSize = (shield, prevCellHeight, targetArea) => (
  dispatch,
  getState
) => {
  const {
    core: { pages, fontCoef },
  } = getState();

  if (shield) {
    const layers = getAllLayers(shield.id, pages);
    const { text_height, transforms } = layers.group.textStyle;
    const matrixRotation =
      transforms?.angle === 90 || transforms?.angle === 270;
    const isContentEmpty = Object.values(layers.page.sections)
      .filter(s => s.shieldId === shield.id)
      .every(item => !item.text);
    const x = matrixRotation ? 'y' : 'x';
    const h = matrixRotation ? 'w' : 'h';
    const width = matrixRotation ? 'height' : 'width';
    const height = matrixRotation ? 'width' : 'height';
    const targetAreaCells = targetArea
      ? Object.values(layers.page.sections)
          .filter(s => s.shieldId === shield.id)
          .filter(
            s =>
              toFixedX(s[x], 5) >= toFixedX(targetArea.l, 5) &&
              toFixedX(s[x] + s[width], 5) <= toFixedX(targetArea.r, 5)
          )
      : Object.values(layers.page.sections);

    targetAreaCells.forEach(section => {
      let fontSize = section.fontSize;
      if (isContentEmpty) {
        fontSize =
          text_height -
          ((shield.primitiveProps[h] / section[height]) * fontCoef).toFixed(1);
      } else {
        fontSize = parseFloat(
          ((section.fontSize * section[height]) / prevCellHeight).toFixed(1)
        );
      }

      const _font = generateFontString(
        section.isBold,
        section.isItalic,
        fontSize,
        section.font
      );
      const w_width = measureTextWidth('W', _font);
      if (section[width] <= w_width) {
        fontSize = toFixedX(
          (section.fontSize * (section[width] * 0.8)) / w_width,
          1
        );
      }

      dispatch(updateSectionProp(section, 'fontSize', fontSize, false));
    });
  }
};

export const detectTargetDeviceThunk = () => (dispatch, getState) => {
  switch (currentEnvironment) {
    case APP_ENVIRONMENT.XCORE: {
      break;
    }
    case APP_ENVIRONMENT.WEB_COMPONENT: {
      dispatch(setTargetDevice(DESKTOP));
      break;
    }
    case APP_ENVIRONMENT.CLIPX_WEB:
    default: {
      dispatch(setTargetDevice(detectTargetDevice()));
      break;
    }
  }
};

export const setPlatformEnvironment = payload => {
  return { type: SET_PLATFORM_ENVIRONMENT, payload };
};

export const setFontSizeCoef = payload => {
  return { type: SET_FONT_SIZE_COEF, payload };
};

export const setFontSizeCoefThunk = () => (dispatch, getState) => {
  const {
    core: { platform },
  } = getState();
  const fontSizeCoef = calculateFontSizeCoef(platform);

  dispatch(setFontSizeCoef(fontSizeCoef));
};

export const setLineHeightCoef = payload => {
  return { type: SET_LINE_HEIGHT_COEF, payload };
};

export const setLetterSpacingCoef = payload => {
  return { type: SET_LETTER_SPACING_COEF, payload };
};

export const setVariantColorModalOpen = payload => {
  return { type: SET_VARIANT_COLOR_MODAL_OPEN, payload };
};

export const setFloatableInputVisible = payload => {
  return { type: SET_FLOATABLE_INPUT_VISIBLE, payload };
};

export const setPrinting = payload => {
  return { type: SET_PRINTING, payload };
};

export const onMatrixCellWidthChangeClb = width => (dispatch, getState) => {
  const { core: { sectionsSelection, pages, shieldsSelection } } = getState();

  let selectedSection = null;
  let selectedShield = null;

  for (const secId in sectionsSelection) {
    const { section } = getAllLayers(secId, pages);
    selectedSection = section;
    break;
  }

  for (const shId in shieldsSelection) {
    const { shield } = getAllLayers(shId, pages);
    selectedShield = shield;
    break;
  }

  if (!selectedShield && selectedSection) {
    const { shield } = getAllLayers(selectedSection.shieldId, pages);
    selectedShield = shield;
  }

  const { transforms } = selectedShield ? getAllLayers(selectedShield.id, pages).group.textStyle : { transforms: { angle: 0 } };
  const matrixRotation = transforms?.angle > 0;

  if (matrixRotation) {
    if (width < selectedShield.primitiveProps.h && width > 0 && selectedSection.height < selectedShield.primitiveProps.h) {
      dispatch(
        setMatrixSectionWidth({
          section: selectedSection,
          width
        })
      );

      selectedSection.height = width;
    }
  } else {
    if (width < selectedShield.primitiveProps.w && width > 0 && selectedSection.width < selectedShield.primitiveProps.w) {
      dispatch(
        setMatrixSectionWidthThunk({
          section: selectedSection,
          width
        })
      );
    }
  }
};